import React from "react";
import { Clock, MapPin } from "lucide-react";
import { fmtDate, fmtDay } from "../../lib/motion.jsx";

export const STATUS_KEY = {
  planned: "stPlanned", live: "stLive", finished: "stFinished",
  postponed: "stPostponed", cancelled: "stCancelled", forfeit: "stForfeit",
};

export const statusLabel = (t, status) => t(STATUS_KEY[status] || "stPlanned");

const isInkad = (name) => (name || "").toLowerCase().includes("inkad");

export const MatchRow = ({ m, t, lang }) => {
  const played = m.home_score !== null && m.home_score !== undefined;
  return (
    <div className="flex items-center justify-between gap-4 border border-white/10 bg-[#141414] p-4 hover:border-[#CCFF00]/40 transition-colors" data-testid="sport-match-row">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 truncate">
          {fmtDay(m.datetime, lang)} · {fmtDate(m.datetime, lang)} · {m.competition_name}
        </div>
        <div className="flex flex-wrap items-center gap-2 font-bold text-sm">
          {m.home_logo && <img src={m.home_logo} alt="" className="h-5 w-5 object-contain" />}
          <span className={isInkad(m.home_team) ? "text-[#CCFF00]" : ""}>{m.home_team}</span>
          <span className="text-white/30">vs</span>
          {m.away_logo && <img src={m.away_logo} alt="" className="h-5 w-5 object-contain" />}
          <span className={isInkad(m.away_team) ? "text-[#CCFF00]" : ""}>{m.away_team}</span>
        </div>
        {m.venue && (
          <div className="text-xs text-white/40 flex items-center gap-1 mt-1"><MapPin size={11} />{m.venue}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        {m.status === "live" ? (
          <span className="inline-flex items-center gap-1 bg-red-500 px-2 py-1 text-xs font-extrabold uppercase text-white">● {t("stLive")}</span>
        ) : played ? (
          <div>
            <span className="font-mono2 text-xl font-bold text-[#CCFF00]">{m.home_score}–{m.away_score}</span>
            {m.home_pens != null && <div className="text-[10px] text-white/40">pen. {m.home_pens}–{m.away_pens}</div>}
            {m.status === "forfeit" && <div className="text-[10px] uppercase text-red-400">{t("stForfeit")}</div>}
          </div>
        ) : (
          <div>
            <span className="font-mono2 text-sm text-white/70 flex items-center justify-end gap-1"><Clock size={12} /> {m.time}</span>
            <div className="mt-1 text-[10px] uppercase text-white/40">{statusLabel(t, m.status)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export const StandingsTable = ({ rows, t }) => (
  <div className="overflow-x-auto border border-white/10">
    <table className="w-full text-sm" data-testid="sport-standings-table">
      <thead>
        <tr className="bg-[#141414] text-white/40 text-[11px] uppercase tracking-wider">
          {["tPos", "tTeam", "tPl", "tW", "tD", "tL", "tGF", "tGA", "tGD", "tPts"].map((c) => (
            <th key={c} className={`p-3 ${c === "tTeam" ? "text-left" : "text-center"}`}>{t(c)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => (
          <tr key={s.team_external_id}
            className={`border-t border-white/5 ${s.is_inkad ? "bg-[#CCFF00]/10" : "hover:bg-white/5"} transition-colors`}
            data-testid={s.is_inkad ? "standings-inkad-row" : undefined}>
            <td className="p-3 text-center font-mono2 text-white/50">{s.position}</td>
            <td className={`p-3 font-bold ${s.is_inkad ? "text-[#CCFF00]" : ""}`}>
              <span className="flex items-center gap-2">
                {s.logo && <img src={s.logo} alt="" className="h-5 w-5 object-contain" />}{s.name}
              </span>
            </td>
            <td className="p-3 text-center text-white/60">{s.played}</td>
            <td className="p-3 text-center text-white/60">{s.won}</td>
            <td className="p-3 text-center text-white/60">{s.drawn}</td>
            <td className="p-3 text-center text-white/60">{s.lost}</td>
            <td className="p-3 text-center text-white/60">{s.goals_for}</td>
            <td className="p-3 text-center text-white/60">{s.goals_against}</td>
            <td className="p-3 text-center text-white/60">{s.goal_difference > 0 ? `+${s.goal_difference}` : s.goal_difference}</td>
            <td className="p-3 text-center font-mono2 font-bold">{s.points}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
